import { AbstractGenericBaseClass, GenericBaseClass } from '../../utility/mixin.js';
import { Component } from './component.js';
import { ComponentList } from './component-list.js';
import { IEntity } from './entity.js';

export function EntityMixin<TClass, TBase extends GenericBaseClass<TClass> | AbstractGenericBaseClass<TClass>>(BaseClass: TBase) {
  abstract class Result extends BaseClass implements IEntity {
    abstract readonly components: ComponentList;

    hasComponentByTypeId(typeId: string): boolean {
      return this.components.hasComponentByTypeId(typeId);
    }

    mustFindComponentByTypeId<C extends Component>(typeId: string): C {
      return this.components.mustFindComponentByTypeId(typeId);
    }

    findComponentByTypeId<C extends Component>(typeId: string): C | null {
      return this.components.findComponentByTypeId(typeId);
    }

    findOrCreateComponentByTypeId<C extends Component>(typeId: string, creator: () => C): C {
      return this.components.findOrCreateComponentByTypeId(typeId, creator);
    }

    findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
      return this.components.findComponentByInstanceId(instanceId);
    }

    findComponentsByTypeId<C extends Component>(typeId: string): C[] {
      return this.components.findComponentsByTypeId(typeId);
    }

    addComponent<C extends Component>(component: C): void {
      this.components.addComponent(component);
    }

    addComponents(list: Component[]): void {
      this.components.addComponents(list);
    }

    removeComponentByTypeId(typeId: string): boolean {
      return this.components.removeComponentByTypeId(typeId);
    }

    removeComponentByInstanceId(instanceId: string): boolean {
      return this.components.removeComponentByInstanceId(instanceId);
    }

    removeComponentAt(index: number): void {
      this.components.removeComponentAt(index);
    }

    removeAllComponents(): void {
      this.components.removeAllComponents();
    }

    popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
      return this.components.popAllComponentsByTypeId(typeId);
    }

    popComponentByTypeId<C extends Component>(typeId: string): C | null {
      return this.components.popComponentByTypeId(typeId);
    }

    popComponent<C extends Component>(component: C): C | null {
      return this.components.popComponent(component);
    }

    replaceComponent<C extends Component>(replacement: C): boolean {
      return this.components.replaceComponent(replacement);
    }

    addOrReplaceComponent<C extends Component>(component: C): void {
      this.components.addOrReplaceComponent(component);
    }

    mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
      return this.components.mustFindComponentField(componentTypeId, find);
    }

    findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
      return this.components.findComponentField(componentTypeId, find);
    }
  }

  return Result;
}
